import { Ticket } from '@/@types/generated'
import Loading from '@/components/Atoms/Loading'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { getTicket } from '@/services/TicketsServiceExtensions'

interface TicketContextProps {
  ticket: Ticket
  reload: (background?: boolean) => Promise<void>
}

const TicketContext = createContext<TicketContextProps>(undefined!)

export function useTicket() {
  const context = useContext(TicketContext)
  if (context === undefined) {
    throw new Error(
      'useTicket must be used within a TicketContext.Provider route'
    )
  }

  return context
}

export function TicketProvider({
  id,
  children,
  onError,
}: PropsWithChildren<{
  id: Ticket['id']
  onError?: (error: unknown) => void
}>) {
  // State
  const [loading, setLoading] = useState(true)
  const [_ticket, setTicket] = useState<Ticket | null>(null)

  // Load the ticket details
  const reload = useCallback(
    async (background?: boolean) => {
      if (!id) return
      if (!background) setLoading(true)

      try {
        const resp = await getTicket(id)
        setTicket(resp)
      } catch (error) {
        onError?.(error)
      } finally {
        setLoading(false)
      }
    },
    [id, onError]
  )

  // Load ticket details when ID changes
  useEffect(() => {
    reload()
  }, [id, reload])

  // No ticket yet, show loading
  if (loading)
    return (
      <Loading variant="fullscreen" message="Retrieving ticket details..." />
    )
  if (!_ticket) return <div>Ticket not found</div>

  const value: TicketContextProps = { ticket: _ticket, reload }
  return (
    <TicketContext.Provider value={value}>{children}</TicketContext.Provider>
  )
}
